import AddAttachmentButton from '@components/button/AddAttachmentButton'
import { spacing } from '@globals/themes/themeConstants'
import { MediaObjectUpload, MediaObjectUrl } from '@kudos/http-client'
import Tooltip from '@mui/material/Tooltip'
import { SxProps } from '@mui/material/styles'
import ResponsiveScrollingTiledList from '@shared/components/ResponsiveScrollingTiledList'
import HiddenFileInput from '@shared/components/mediaUploads/HiddenFileInput'
import MediaAttachmentTile from '@shared/components/mediaUploads/MediaAttachmentTile'
import { AttachmentType } from '@shared/hooks/useFileMediaUpload'
import {
  MAX_FILE_SIZE_IN_BYTES,
  MAX_FILE_SIZE_IN_MB,
  getZeroSizeValidationMsg,
  isFileSizeGreaterThanZero,
} from '@shared/uploads/ImageUploads.util'
import { MAX_ATTACHMENTS } from '@shared/wallPost/constants'
import { T, useT } from '@transifex/react'
import { FC, useRef, useState } from 'react'
import {
  Control,
  FieldValues,
  UseControllerProps,
  useFieldArray,
  useFormContext,
  useWatch,
  type Path,
} from 'react-hook-form'
import AttachmentField from './AttachmentField'
import ErrorText from './ErrorText'

export const ATTACHMENT_IMAGE_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
export const ATTACHMENT_OTHER_MIME_TYPES = [
  'image/gif',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
]

type AttachmentField = {
  id: string
  attachmentSrc?: MediaObjectUrl
  attachmentType: AttachmentType
  file?: File | null
}

type AttachmentsFieldProps = UseControllerProps & {
  mutationFn: (params: {
    originalFilename: string
    contentType: string
  }) => Promise<MediaObjectUpload>
  showLabel?: boolean
  hideUploadButton?: boolean
  buttonStyles?: SxProps
}

const AttachmentsField: FC<AttachmentsFieldProps> = ({
  control: _controlProp,
  mutationFn,
  hideUploadButton = false,
  showLabel = true,
  buttonStyles,
}) => {
  const t = useT()
  const {
    setValue,
    setError,
    clearErrors,
    formState,
    control: formContextControl,
  } = useFormContext()
  const { errors } = formState
  const rootError = errors.root
  const inputFileRef = useRef<HTMLInputElement>(null)
  const [maxSizeError, setMaxSizeError] = useState<boolean>(false)
  const [invalidTypeError, setInvalidTypeError] = useState<boolean>(false)
  const [emptyFileError, setEmptyFileError] = useState<boolean>(false)

  // Use formContext.control to ensure we use the same control instance as useFroalaAttachments
  const control = formContextControl
  const gif = useWatch({ control, name: 'gif' })

  // Watch the attachments field to get the current form state
  const watchedAttachments = useWatch({
    control,
    name: 'attachments',
  })

  const { append, remove } = useFieldArray({
    control,
    name: 'attachments',
  })

  // useFieldArray's fields can be out of sync, but useWatch correctly reflects form state
  // Use watchedAttachments for rendering, but still use append/remove from useFieldArray for mutations
  // React Hook Form's useFieldArray.append automatically adds an id field, so items should already have it
  const attachments = (Array.isArray(watchedAttachments) ? watchedAttachments : []).map(
    (attachment, index) => {
      // If attachment already has an id (from useFieldArray.append), use it
      // Otherwise, generate a stable id based on attachment properties (fallback should rarely be needed)
      const existingId = (attachment as AttachmentField).id
      if (existingId) {
        return attachment as AttachmentField
      }
      // Fallback: generate ID from file name or URL (more stable than index-based)
      const id = `attachment-${attachment.file?.name || attachment.attachmentSrc?.url || index}`
      return {
        ...attachment,
        id,
      } as AttachmentField
    },
  )

  const handleDeleteAttachmentClick = (index: number) => {
    remove(index)
  }

  const handleAddAttachmentClick = () => {
    setMaxSizeError(false)
    setInvalidTypeError(false)
    setEmptyFileError(false)
    // mimics a click on the hidden file input
    inputFileRef?.current?.click()
  }

  const handleDeleteGifClick = () => {
    setValue('gif', undefined, { shouldValidate: true })
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0]
      // Validate file type
      const attachmentMimeTypes = [...ATTACHMENT_IMAGE_MIME_TYPES, ...ATTACHMENT_OTHER_MIME_TYPES]
      const isValidType = attachmentMimeTypes.includes(selectedFile.type)
      if (!isValidType) {
        setInvalidTypeError(true)
        e.target.value = ''
        return
      }
      if (!isFileSizeGreaterThanZero(selectedFile)) {
        setEmptyFileError(true)
        e.target.value = ''
        return
      }
      // Validate file size
      const isValidSize = selectedFile.size <= MAX_FILE_SIZE_IN_BYTES
      if (!isValidSize) {
        setMaxSizeError(true)
      } else {
        append({
          mediaObjectId: '',
          file: selectedFile,
          attachmentType: selectedFile.type.startsWith('image/') ? 'image' : 'document',
        })
      }
    }
  }

  return (
    <>
      <HiddenFileInput
        ref={inputFileRef}
        type="file"
        accept="image/jpg, .png, .webp, .pdf, .docx, .pptx, .gif"
        id="fileUploadAttachment"
        onChange={handleFileInputChange}
      />
      <ResponsiveScrollingTiledList marginTop={spacing(2)} scrollToLastItem>
        {!!attachments.length &&
          attachments.map((field: AttachmentField, index: number) => (
            <AttachmentField
              key={field.id}
              name="attachments"
              control={control as unknown as Control<FieldValues>}
              attachmentSrc={field.attachmentSrc}
              file={field.file}
              index={index}
              attachmentType={field.attachmentType}
              onDelete={handleDeleteAttachmentClick}
              onError={() => remove(index)}
              mutationFn={mutationFn}
              setFormError={setError}
              clearFormErrors={clearErrors}
              apiErrorFieldPaths={[] as Path<FieldValues>[]}
            />
          ))}
        {!!gif && (
          <MediaAttachmentTile
            src={gif}
            alt={t('GIF attachment')}
            onDelete={handleDeleteGifClick}
          />
        )}
      </ResponsiveScrollingTiledList>
      {!hideUploadButton && (
        <Tooltip
          id="tooltipAddAttachment"
          sx={{ width: 2000 }}
          title={
            <>
              <T _str="JPG, PNG, GIF, WebP, PDF, DOCX, PPTX" />
              <br />
              <T _str="{maxSize}MB Max file size" maxSize={MAX_FILE_SIZE_IN_MB} />
            </>
          }
          placement="bottom-start"
          slotProps={{
            tooltip: {
              sx: {
                maxWidth: 500,
                width: 'auto',
                whiteSpace: 'normal',
              },
            },
          }}>
          <span>
            <AddAttachmentButton
              buttonId="btnAddAnnouncementAttachment"
              ariaLabelledBy="tooltipAddAnnouncementAttachment"
              onClick={handleAddAttachmentClick}
              disabled={attachments?.length >= MAX_ATTACHMENTS}
              showLabel={showLabel}
              buttonStyles={buttonStyles}
            />
          </span>
        </Tooltip>
      )}
      {maxSizeError && (
        <ErrorText>
          <T
            _str="The file exceeds the {maxSize}MB size limit, upload a smaller file"
            maxSize={MAX_FILE_SIZE_IN_MB}
          />
        </ErrorText>
      )}
      {invalidTypeError && (
        <ErrorText>
          <T _str="Attachment must be JPG, PNG, GIF, WEBP, PDF, DOCX or PPTX" />
        </ErrorText>
      )}
      {emptyFileError && <ErrorText>{getZeroSizeValidationMsg(t)}</ErrorText>}
      {!!rootError?.message && <ErrorText>{rootError.message}</ErrorText>}
    </>
  )
}

export default AttachmentsField
